/**
 * Per-node live item counts folded from the SSE stream.
 *
 * WHY THIS EXISTS (the bug it fixes): the live event buffer
 * (`lib/eventStream.tsx`) is a **capped, newest-first RING of 200 events shared
 * by the whole app**. The editor used to recompute each node's count by summing
 * `node_finished.items_out` over that buffer on every pass — correct only while
 * the buffer still holds every event of the run. A chatty run (100 articles ×
 * ~6 sub-nodes × 2 events ≈ 1200 events) EVICTS its own early events, so the
 * recomputed sum SHRANK and the per-edge pills vanished mid-run — the "gli item
 * spariscono" report.
 *
 * So: ACCUMULATE instead of recompute. Each event is folded exactly ONCE
 * (deduped by `LiveEvent.id`, which is stable per event), into a caller-owned
 * accumulator that survives eviction. The caller resets it per run.
 */

/** The subset of a `LiveEvent` this fold reads. */
export interface CountableEvent {
  id: string;
  kind: string;
  execution_id?: string;
  /** Set on a SUB-WORKFLOW's node events: the run that launched it. */
  parent_execution_id?: string;
  workflow_id?: string;
  node_id?: string;
  items_out?: number;
}

/** Does this event belong to `executionId` — or to a sub-run it launched? */
export function matchesRun(
  e: { execution_id?: string; parent_execution_id?: string },
  executionId: string,
  includeSubRuns = false,
): boolean {
  if (e.execution_id === executionId) return true;
  return includeSubRuns === true && e.parent_execution_id === executionId;
}

/** Caller-owned state, reset at the start of each run. */
export interface LiveCountState {
  /** Event ids already folded — the dedup that makes accumulation safe. */
  seen: Set<string>;
  /** key → summed items_out. */
  counts: Record<string, number>;
}

export function newLiveCountState(): LiveCountState {
  return { seen: new Set(), counts: {} };
}

export interface FoldOptions {
  /** Only fold events of this execution. */
  executionId: string | null;
  /**
   * Build the accumulator key for an event, or null to skip it. The editor
   * keys by `node_id`; the sub-workflow dialog keys by `workflow_id:node_id`
   * (parent, child and sibling sub-workflows all run under ONE execution_id
   * and their auto-generated node ids collide).
   */
  keyOf: (e: CountableEvent) => string | null;
  /**
   * Also fold events emitted by a SUB-WORKFLOW that `executionId` launched —
   * matched on the event's `parent_execution_id` back-pointer.
   *
   * OPT-IN, and it must stay that way: a sub-workflow runs under its own
   * execution now, but its node ids COLLIDE with the parent's, and the editor
   * keys purely by `node_id`. Folding child events there would add the child's
   * items onto a same-named parent node. Only the drill-in enables it — it also
   * narrows `keyOf` to the child's own workflow.
   */
  includeSubRuns?: boolean;
}

/**
 * Fold every not-yet-seen `node_finished` into `state`. Returns true when a
 * count changed, so the caller can skip a no-op re-render.
 *
 * A loop-body node finishes once PER BATCH under the same execution, so its
 * `items_out` are SUMMED — which is exactly why the dedup has to be per EVENT
 * (not per node): re-folding a batch's event would inflate the total.
 */
export function foldLiveCounts(
  events: readonly CountableEvent[],
  state: LiveCountState,
  { executionId, keyOf, includeSubRuns }: FoldOptions,
): boolean {
  let changed = false;
  for (const e of events) {
    if (e.kind !== "node_finished") continue;
    if (!e.id || state.seen.has(e.id)) continue;
    if (executionId != null && !matchesRun(e, executionId, includeSubRuns)) continue;
    if (e.items_out == null) continue;
    const key = keyOf(e);
    if (key == null) continue;
    state.seen.add(e.id);
    state.counts[key] = (state.counts[key] ?? 0) + e.items_out;
    changed = true;
  }
  return changed;
}

/**
 * Accumulators kept at MODULE scope, keyed per run — so they outlive the React
 * tree that reads them.
 *
 * WHY: a component-owned `useRef` accumulator dies on UNMOUNT, and the events it
 * had folded are long gone from the 200-event ring — they can never be re-read.
 * So closing the sub-workflow drill-in and reopening it mid-run restarted the
 * count from whatever few events still happened to be in the buffer, and the
 * number came back LOWER than before ("chiudo e riapro e il numero è inferiore").
 * The same reset also has to happen between runs — an accumulator carried across
 * a re-run would ADD run #2's items onto run #1's total. Keying by run gives both:
 * reopening the SAME run resumes its accumulator, a NEW run starts clean.
 */
const _RUN_STATES = new Map<string, LiveCountState>();

/** How many runs' accumulators to keep — a bound, not a policy. */
const _MAX_TRACKED_RUNS = 8;

/** The accumulator for `runKey`, created on first use. */
export function stateForRun(runKey: string): LiveCountState {
  const existing = _RUN_STATES.get(runKey);
  if (existing) return existing;
  const fresh = newLiveCountState();
  _RUN_STATES.set(runKey, fresh);
  // Map preserves insertion order → the first key is the oldest run.
  while (_RUN_STATES.size > _MAX_TRACKED_RUNS) {
    const oldest = _RUN_STATES.keys().next().value;
    if (oldest === undefined || oldest === runKey) break;
    _RUN_STATES.delete(oldest);
  }
  return fresh;
}

/** Test-only: drop every tracked run. */
export function resetRunStates(): void {
  _RUN_STATES.clear();
}
